import React, { useState, useRef, useEffect } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import FAQ from '../components/FAQ';
import GettingStarted from '../components/GettingStarted';
import PrivacyPolicy from '../components/PrivacyPolicy';
import { trackFeature } from '../utils/analytics';
import ContactUs from '../components/ContactUs';

// A simple TabPanel component for MUI tabs.
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Accessibility props helper (for screen readers).
function a11yProps(index) {
  return {
    id: `help-tab-${index}`,
    'aria-controls': `help-tabpanel-${index}`,
  };
}

const tabKeys = ['gettingstarted', 'faq', 'contact', 'privacy'];

export default function Help() {
  const [tabValue, setTabValue] = useState(0);

  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      // skip on first render
      hasLoaded.current = true;
      return;
    }
    // only fire when the user actually switches tabs
    trackFeature('page_view', {
      page_path: `/help/${tabKeys[tabValue]}`,
      tab: tabKeys[tabValue],
    });
  }, [tabValue]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        maxWidth: '95%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '0px',

        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      {/* Similar styling to your Management page */}
      <Box sx={{
        maxWidth: {
          xs: '100%',
          sm: '100%',
          md: '60%',
        },
        margin: '0 auto 0 auto'
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="inherit"
          variant="fullWidth"
          sx={{
            color: (theme) => theme.palette.secondary.light,
            '& .MuiTabs-indicator': {
              backgroundColor: (theme) => theme.palette.warning.light,
            },
          }}
        >
          <Tab
            label="Getting Started"
            sx={{
              color: (theme) => theme.palette.primary.light,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(0)}
          />
          <Tab
            label="Frequently Asked Questions"
            sx={{
              color: (theme) => theme.palette.primary.light,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(1)}
          />
          <Tab
            label="Contact Us / Bug Reports"
            sx={{
              color: (theme) => theme.palette.primary.light,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(2)}
          />
          <Tab
            label="Privacy Policy"
            sx={{
              color: (theme) => theme.palette.primary.light,
              '&.Mui-selected': {
                color: (theme) => theme.palette.warning.main,
              },
            }}
            {...a11yProps(3)}
          />
        </Tabs>
      </Box>

      {/* TabPanels for each section */}
      <TabPanel value={tabValue} index={0}>
        <GettingStarted />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <FAQ />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <ContactUs />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <PrivacyPolicy />
      </TabPanel>
    </Box>
  );
}
